import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react"; // Replacing MUI icons
import { UploadButtonViewModel } from "./upload-button.view-model";
import { IFileInfo } from "src/types/models/apiModels";
import { useAuth } from "src/components/organisms/auth/auth-context";
import ButtonPro from "src/components/atoms/primary-button/primary-button";

interface UploadButtonProps {
	onUploadSuccess: (file: IFileInfo) => void;
	onClear?: () => void;
	buttonType?: "Link" | "Primary" | "Image" | "File";
	borderRadius?: string; // Optional border radius prop
	defaultImage?: string;
	addTrustShield?: boolean;
	showBorder?: boolean;
}

const UploadButton: React.FC<UploadButtonProps> = ({
	onUploadSuccess,
	onClear,
	buttonType,
	borderRadius = "8px", // Default border radius
	defaultImage,
	addTrustShield = false,
	showBorder = true,
}) => {
	const blankImage = "https://placehold.co/150x150.png";
	const { user } = useAuth();
	const [fileSrc, setFileSrc] = useState<IFileInfo | undefined>();
	const [uploading, setUploading] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [uploadController, setUploadController] =
		useState<AbortController | null>(null);
	const [imageUrl, setImageUrl] = useState<string | undefined>(defaultImage);

	// ViewModel reference and file input reference
	const viewModel = useRef<UploadButtonViewModel | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	// Setup ViewModel on load
	useEffect(() => {
		if (user && user.accessToken) {
			viewModel.current = new UploadButtonViewModel(user.accessToken);
		}
	}, []);

	// Update image when default image changes
	useEffect(() => {
		if (defaultImage) {
			setImageUrl(() => defaultImage);
		}
	}, [defaultImage]);

	// Update image when file source changes
	useEffect(() => {
		if (fileSrc?.signedUrl) {
			setImageUrl(fileSrc.signedUrl);
			onUploadSuccess(fileSrc);
		} else if (imageUrl) {
			setImageUrl(imageUrl);
		} else {
			setImageUrl(blankImage);
		}
	}, [fileSrc]);

	// Handle File Upload
	const handleFileChange = async (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;

		setUploading(true);
		setUploadError(null);

		const controller = new AbortController();
		setUploadController(controller);

		try {
			let fileToUpload = selectedFile;

			// ✅ Only modify image if addTrustShield is true
			if (addTrustShield) {
				fileToUpload = await addShieldToImage(selectedFile);
			}

			const uploadedFile =
				await viewModel.current?.uploadFileAsync(fileToUpload);

			if (uploadedFile) {
				const fileInfo: IFileInfo = {
					signedUrl: uploadedFile.signedUrl,
					fileName: selectedFile.name,
				};
				setFileSrc(fileInfo);
			} else {
				setUploadError("Upload failed. Please try again.");
			}
		} catch (error) {
			console.error("Error uploading file:", error);
			setUploadError("An error occurred during upload.");
		} finally {
			setUploading(false);
			setUploadController(null);
		}
	};

	// ✅ Modify image before uploading it
	const addShieldToImage = async (file: File): Promise<File> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);

			reader.onload = async (event) => {
				const imageUrl = event.target?.result as string;
				const img = new Image();
				img.crossOrigin = "anonymous"; // ✅ Fixes CORS issues
				img.src = imageUrl;

				await new Promise((resolve) => (img.onload = resolve));

				const canvas = document.createElement("canvas");
				const ctx = canvas.getContext("2d");
				if (!ctx) return reject("Canvas context not available");

				const canvasSize = 180;
				const contactImageSize = 140;

				canvas.width = canvasSize;
				canvas.height = canvasSize;
				ctx.clearRect(0, 0, canvasSize, canvasSize);

				const offsetX = (canvasSize - contactImageSize) / 2;
				const offsetY = (canvasSize - contactImageSize) / 2;

				ctx.save();
				ctx.beginPath();
				ctx.arc(
					canvasSize / 2,
					canvasSize / 2,
					contactImageSize / 2,
					0,
					Math.PI * 2
				);
				ctx.clip();
				ctx.drawImage(
					img,
					offsetX,
					offsetY,
					contactImageSize,
					contactImageSize
				);
				ctx.restore();

				// ✅ Hardcoded SVG shield
				const shieldSvg = `
		  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="green" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-shield-check"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>`;

				const shieldImage = new Image();
				const svgBlob = new Blob([shieldSvg], {
					type: "image/svg+xml",
				});
				const svgUrl = URL.createObjectURL(svgBlob);

				shieldImage.src = svgUrl;
				await new Promise((resolve) => (shieldImage.onload = resolve));

				const shieldSize = 45;
				const shieldX = canvasSize - shieldSize - 30;
				const shieldY = 15;

				ctx.shadowColor = "rgba(0, 0, 0, 0.4)";
				ctx.shadowBlur = 6;
				ctx.shadowOffsetX = 2;
				ctx.shadowOffsetY = 3;
				ctx.drawImage(
					shieldImage,
					shieldX,
					shieldY,
					shieldSize,
					shieldSize
				);

				// ✅ Convert back to File object
				canvas.toBlob((blob) => {
					if (!blob) return reject("Failed to create blob");
					const modifiedFile = new File([blob], file.name, {
						type: "image/png",
					});
					resolve(modifiedFile);
				}, "image/png");
			};
		});
	};

	// Cancel Upload
	const handleCancelUpload = () => {
		if (uploadController) {
			uploadController.abort();
		}
	};

	// Remove Image
	const handleRemoveImage = () => {
		setFileSrc({
			fileName: "unknown",
			signedUrl: blankImage,
		});
		setUploadError(null);
		onClear && onClear();
	};

	// Trigger File Input
	const triggerFileInput = () => {
		fileInputRef.current?.click();
	};

	return (
		<div className="flex flex-col items-center justify-center">
			{/* Image Preview Container */}
			<div className="relative">
				{/* Image Preview */}
				{buttonType !== "File" && (
					<div
						className={`p-2 rounded-2xl ${showBorder ? "border-4 border-gray-400" : ""}`}
					>
						<img
							src={imageUrl}
							alt="Uploaded"
							className="h-[150px] w-[150px] rounded-2xl"
							style={{ borderRadius }}
							onClick={
								buttonType === "Image"
									? triggerFileInput
									: undefined
							}
						/>
					</div>
				)}

				{/* Clear Button (X Icon) */}
				{fileSrc &&
					buttonType !== "File" &&
					fileSrc.fileName !== "unknown" && (
						<button
							className="absolute -top-1 -right-2 bg-gray-800 text-white p-1 rounded-full shadow-md hover:bg-red-600"
							onClick={handleRemoveImage}
						>
							<X className="w-4 h-4" />
						</button>
					)}
			</div>

			<div className="flex flex-col items-center justify-center gap-4">
				{/* Upload Button */}
				{(buttonType === "Primary" || buttonType === "File") && (
					<div className="flex items-center justify-start w-full">
						<div className="max-w-96 mt-3">
							<ButtonPro
								onClick={triggerFileInput}
								label={fileSrc ? "Change" : "Upload"}
								disabled={uploading}
							></ButtonPro>
						</div>
					</div>
				)}

				{fileSrc && (
					<div className="flex items-center justify-start w-full gap-2">
						{buttonType === "File" && (
							<>
								<span className="font-Poppins font-medium text-sm">
									{fileSrc.fileName}
								</span>
								<button
									className="bg-gray-800 text-white p-1 rounded-full shadow-md hover:bg-red-600"
									onClick={handleRemoveImage}
								>
									<X className="w-2 h-2" />
								</button>
							</>
						)}
					</div>
				)}
			</div>
			{/* Hidden File Input */}
			<input
				type="file"
				className="hidden"
				onChange={handleFileChange}
				ref={fileInputRef}
			/>

			{/* Progress Bar */}
			{uploading && (
				<div className="flex items-center gap-2 w-full">
					<div className="w-full bg-gray-200 rounded-full h-2.5">
						<div
							className="bg-sky-600 h-2.5 rounded-full"
							style={{ width: "75%" }} // Mock progress
						></div>
					</div>
					<button
						onClick={handleCancelUpload}
						className="text-red-600 hover:text-red-800"
					>
						<X className="w-5 h-5" />
					</button>
				</div>
			)}

			{/* Error Message */}
			{uploadError && (
				<p className="text-red-600 text-sm">{uploadError}</p>
			)}
		</div>
	);
};

export default UploadButton;
